import { z } from 'zod'
import { slugSchema } from './common.js'
import { gallerySchema } from './media.js'

/** RF01.1 - os quatro tipos de publicacao que a paroquia produz. */
export const postTypeSchema = z.enum(['NOTICIA', 'AVISO', 'COMUNICADO', 'NOTA_PESAR'])
export type PostType = z.infer<typeof postTypeSchema>

export const POST_TYPE_LABELS: Record<PostType, string> = {
  NOTICIA: 'Noticia',
  AVISO: 'Aviso',
  COMUNICADO: 'Comunicado oficial',
  NOTA_PESAR: 'Nota de pesar',
}

export const postStatusSchema = z.enum(['DRAFT', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED'])
export type PostStatus = z.infer<typeof postStatusSchema>

export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  DRAFT: 'Rascunho',
  SCHEDULED: 'Agendada',
  PUBLISHED: 'Publicada',
  ARCHIVED: 'Arquivada',
}

export const categorySchema = z.object({
  name: z.string().min(2, 'Informe o nome da categoria').max(80).trim(),
  slug: slugSchema.optional(),
  description: z.string().max(300).trim().optional(),
  /** Cor de destaque na listagem publica (hex). */
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use uma cor no formato #RRGGBB')
    .optional(),
})

export const tagSchema = z.object({
  name: z.string().min(2, 'Informe o nome da tag').max(60).trim(),
  slug: slugSchema.optional(),
})

const richTextSchema = z
  .string()
  .max(200_000, 'Conteudo muito longo')
  .describe('HTML sanitizado produzido pelo editor TipTap')

export const postInputSchema = z
  .object({
    type: postTypeSchema.default('NOTICIA'),
    title: z.string().min(4, 'O titulo deve ter ao menos 4 caracteres').max(200).trim(),
    slug: slugSchema.optional(),
    /** Resumo. Alimenta a listagem, o RSS e a og:description do post. */
    excerpt: z.string().max(320, 'O resumo deve ter no maximo 320 caracteres').trim().optional(),
    body: richTextSchema,
    coverMediaId: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    tagIds: z.array(z.string()).max(12, 'No maximo 12 tags').default([]),
    /**
     * Anexos em PDF (RF01.1). Carregam rotulo proprio porque o arquivo que sai
     * do scanner da secretaria se chama "doc 001.pdf" - o fiel precisa ler
     * "Edital da Festa do Padroeiro 2026".
     */
    attachments: z
      .array(
        z.object({
          mediaId: z.string().min(1),
          label: z.string().min(2, 'Descreva o anexo').max(160).trim(),
        }),
      )
      .max(10, 'No maximo 10 anexos')
      .default([]),

    /**
     * Galeria de fotos da publicacao.
     *
     * A ordem do array e a ordem na pagina. Separada da imagem de capa: a capa
     * alimenta a listagem e o card do WhatsApp, a galeria conta o acontecimento.
     */
    gallery: gallerySchema,
    status: postStatusSchema.default('DRAFT'),
    /** Obrigatorio quando status = SCHEDULED; define o momento da publicacao. */
    publishedAt: z.iso.datetime({ offset: true }).nullable().optional(),
    /** Fixa o post no topo da home. */
    pinned: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.status === 'SCHEDULED') {
      if (!data.publishedAt) {
        ctx.addIssue({
          code: 'custom',
          path: ['publishedAt'],
          message: 'Informe a data e hora do agendamento',
        })
        return
      }
      if (new Date(data.publishedAt) <= new Date()) {
        ctx.addIssue({
          code: 'custom',
          path: ['publishedAt'],
          message: 'O agendamento precisa ser no futuro',
        })
      }
    }
  })

export type PostInput = z.infer<typeof postInputSchema>

export const postListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(50).default(12),
  type: postTypeSchema.optional(),
  status: postStatusSchema.optional(),
  category: z.string().optional(),
  tag: z.string().optional(),
  /** Busca textual em titulo e resumo. */
  q: z.string().max(120).trim().optional(),
})

export type PostListQuery = z.infer<typeof postListQuerySchema>

/**
 * RF01.3 - aviso urgente com destaque dinamico na home.
 *
 * A severidade controla a cor e a insistencia da faixa. URGENTE existe para o
 * caso que motivou o requisito: alteracao repentina de horario de missa.
 */
export const noticeSeveritySchema = z.enum(['INFO', 'ATENCAO', 'URGENTE'])
export type NoticeSeverity = z.infer<typeof noticeSeveritySchema>

export const NOTICE_SEVERITY_LABELS: Record<NoticeSeverity, string> = {
  INFO: 'Informativo',
  ATENCAO: 'Atencao',
  URGENTE: 'Urgente',
}

export const urgentNoticeInputSchema = z
  .object({
    message: z.string().min(5, 'Escreva a mensagem do aviso').max(280).trim(),
    severity: noticeSeveritySchema.default('ATENCAO'),
    /** Link opcional para a noticia que detalha o aviso. */
    linkUrl: z.union([z.url('Informe uma URL valida'), z.literal('')]).optional(),
    linkLabel: z.string().max(60).trim().optional(),
    startsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    endsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    active: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.startsAt && data.endsAt && new Date(data.endsAt) <= new Date(data.startsAt)) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'O fim da vigencia deve ser depois do inicio',
      })
    }
    if (data.linkLabel && !data.linkUrl) {
      ctx.addIssue({
        code: 'custom',
        path: ['linkUrl'],
        message: 'Informe a URL do link ou remova o texto do link',
      })
    }
  })

export type UrgentNoticeInput = z.infer<typeof urgentNoticeInputSchema>
